import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Grid,
    Card,
    CardMedia,
    CircularProgress,
    IconButton,
    Checkbox,
    Tooltip, // Imported Checkbox for primary selection
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import CheckCircleIcon from "@mui/icons-material/CheckCircle"; // Solid circle for checked state
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked"; // Outline circle for unchecked state
import configServ from "../../services/config";
import { useSelector } from "react-redux";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";

const ImageDetails = ({ employeeId, changes }) => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const { company_id } = useSelector((state) => state.GlobalVariables);

    // FETCH IMAGES FROM API
    const fetchEmployeeImages = async () => {
        try {
            const payload = { employee_id: employeeId, company_id: company_id };
            setLoading(true);
            const response = await configServ.getEmployeeImages(payload);

            if (response.status) {
                setImages(response.data);
            } else {
                setImages([]);
            }
        } catch (error) {
            console.error("Error fetching images:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEmployeeImages();
    }, [employeeId, changes]);

    // HANDLE DELETE
    const handleDelete = async (id) => {
        if (!window.confirm("Do you want to delete this image?")) return;
        try {
            const payload = { id: id, company_id: company_id };
            const response = await configServ.deleteEmployeeImage(payload);
            if (response.status) {
                fetchEmployeeImages();
            }
        } catch (err) {
            console.error("Error deleting image:", err);
        }
    };

    // HANDLE PRIMARY IMAGE CHANGE
    const handlePrimaryChange = async (id) => {
        try {
            const payload = {
                company_id: company_id, // Fixed typos from 'comapny_id' to standard
                image_id: id,
                employee_id: employeeId,
                is_primary: true
            };
            const response = await configServ.updatePrimaryImage(payload);
            if (response.status) {
                fetchEmployeeImages(); // Reload data to show updated checkboxes status
            }
        } catch (err) {
            console.error("Error updating primary image:", err);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress size={36} />
            </Box>
        );
    }

    return (
        <Box sx={{ py: 1 }}>
            {/* HEADER SECTION */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h6" fontWeight="700" color="#1e293b">
                        Uploaded Documents
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Total {images.length} images verified
                    </Typography>
                </Box>
            </Box>

            {/* IMAGES GRID */}
            {images.length > 0 ? (
                <Grid container spacing={2}>
                    {images.map((item) => {
                        // Checking if the current image is marked as primary (handles boolean true or integer 1)
                        const isPrimaryImage = item.is_primary === true || item.is_primary === 1 || item.is_primary === "1";

                        return (
                            <Grid item xs={12} sm={6} md={4} key={item.id}>
                                <Card
                                    sx={{
                                        position: "relative",
                                        borderRadius: "14px",
                                        boxShadow: isPrimaryImage
                                            ? "0 4px 20px rgba(34, 197, 94, 0.15)"
                                            : "0 4px 12px rgba(0,0,0,0.05)",
                                        overflow: "hidden",
                                        // Dynamic Border Highlight: Active primary cards look distinct
                                        border: isPrimaryImage ? "2px solid #22c55e" : "1px solid #e2e8f0",
                                        transition: "all 0.2s ease",
                                        "&:hover .action-overlay": { opacity: 1 },
                                        "&:hover .checkbox-overlay": { opacity: 1 }
                                    }}
                                >
                                    {/* PRIMARY SELECTION CHECKBOX (TOP LEFT) */}
                                    <Tooltip
                                        title="Primary Image"
                                        arrow
                                        placement="top"
                                        enterDelay={200} // Halki si delay taaki direct touch par na pop ho
                                    >
                                        <Box
                                            className="checkbox-overlay"
                                            sx={{
                                                position: "absolute",
                                                top: 8,
                                                left: 8,
                                                zIndex: 2,
                                                // Primary wali hamesha dikhegi, baaki hover par show hongi desktop pe
                                                opacity: isPrimaryImage ? 1 : { xs: 1, sm: 0.8 },
                                                transition: "opacity 0.2s ease",
                                                bgcolor: "rgba(255, 255, 255, 0.9)",
                                                borderRadius: "50%",
                                                width: 32,
                                                height: 32,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
                                            }}
                                        >
                                            <Checkbox
                                                size="small"
                                                color="success" // Beautiful Green Accent
                                                checked={isPrimaryImage}
                                                icon={<RadioButtonUncheckedIcon sx={{ fontSize: 18 }} />}
                                                checkedIcon={<CheckCircleIcon sx={{ fontSize: 18, color: "#22c55e" }} />}
                                                onChange={() => handlePrimaryChange(item.id)}
                                                sx={{ p: 0 }}
                                            />
                                        </Box>



                                    </Tooltip>


                                    {/* CARD MEDIA IMAGE */}
                                    <Zoom>
                                        <CardMedia
                                            component="img"
                                            image={item.image_url}
                                            alt={`Employee Doc ${item.id}`}
                                            sx={{
                                                height: { xs: "200px", sm: "180px" },
                                                width: "100%",
                                                objectFit: "cover",
                                                transition: "transform 0.3s ease",
                                                "&:hover": { transform: "scale(1.03)" },
                                            }}
                                        />
                                    </Zoom>


                                    {/* DESCRIPTION WRAPPER */}
                                    <Box sx={{ p: 1.5, bgcolor: "#fff" }}>
                                        <Typography variant="caption" fontWeight="600" color="text.secondary">
                                            Description: {item.description || "No description"}
                                        </Typography>
                                    </Box>

                                    {/* DELETE ACTION BUTTON (TOP RIGHT) */}
                                    <Box
                                        className="action-overlay"
                                        sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            zIndex: 2,
                                            opacity: { xs: 1, sm: 0 },
                                            transition: "opacity 0.2s ease",
                                            bgcolor: "rgba(255, 255, 255, 0.9)",
                                            borderRadius: "50%",
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDelete(item.id)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            ) : (
                /* NO IMAGES FALLBACK CONTAINER */
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 6,
                        px: 2,
                        border: "2px dashed #cbd5e1",
                        borderRadius: "16px",
                        bgcolor: "#f8fafc",
                        textAlign: "center",
                    }}
                >
                    <Box sx={{ p: 2, borderRadius: "50%", bgcolor: "#f1f5f9", color: "#94a3b8", mb: 1.5, display: "flex" }}>
                        <ImageIcon fontSize="large" />
                    </Box>
                    <Typography variant="subtitle2" fontWeight="600" color="#475569">
                        No Images Found
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default ImageDetails;